import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';
import './index.css';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPassword from './pages/ForgotPassword';
import EntryConfirmation from './pages/EntryConfirmation';
import QRCodePage from './pages/QRCodePage';
import Dashboard from './pages/Dashboard';
import OrganizationSetup from './pages/OrganizationSetup';
import ShowCreate from './pages/ShowCreate';
import ShowList from './pages/ShowList';
import ShowDetail from './pages/ShowDetail';
import EntryForm from './pages/EntryForm';
import ScoringInterface from './pages/ScoringInterface';
import LiveResults from './pages/LiveResults';
import PaymentSuccess from './pages/PaymentSuccess';
import ShowResults from './pages/ShowResults';
import SubscriptionManagement from './pages/SubscriptionManagement';
import OrgLeaderboard from './pages/OrgLeaderboard';
import OrgPointsAdmin from './pages/OrgPointsAdmin';
import OrgStandingsPublic from './pages/OrgStandingsPublic';
import RiderHistory from './pages/RiderHistory';
import SeriesManage from './pages/SeriesManage';
import SeriesDetail from './pages/SeriesDetail';
import SeriesList from './pages/SeriesList';
import ShowEdit from './pages/ShowEdit';
import SubscriptionAdmin from './pages/SubscriptionAdmin';
import ManageEntries from './pages/ManageEntries';
import RiderProfile from './pages/RiderProfile';
import PublishedResults from './pages/PublishedResults';
import KioskLogin from './pages/KioskLogin';
import KioskEntry from './pages/KioskEntry';
import KioskTimekeeper from './pages/KioskTimekeeper';
import KioskOffice from './pages/KioskOffice';
import { InstallPrompt } from './components/InstallPrompt';
import { OfflineBanner } from './components/OfflineBanner';
import { VersionBanner } from './components/VersionBanner';
import { GlobalNav } from './components/GlobalNav';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PlatformSettings from './pages/PlatformSettings';
import ProducerDisputes from './pages/ProducerDisputes';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = window.location;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="shimmer h-12 w-12 rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`} />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization/setup"
        element={
          <ProtectedRoute>
            <OrganizationSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shows/create"
        element={
          <ProtectedRoute>
            <ShowCreate />
          </ProtectedRoute>
        }
      />
      <Route path="/shows" element={<ShowList />} />
      <Route
        path="/shows/:id/edit"
        element={
          <ProtectedRoute>
            <ShowEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shows/:id/manage-entries"
        element={
          <ProtectedRoute>
            <ManageEntries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <RiderProfile />
          </ProtectedRoute>
        }
      />
      <Route path="/shows/:id" element={<ShowDetail />} />
      <Route path="/shows/:id/enter" element={<EntryForm />} />
      <Route path="/shows/:showId/confirmation" element={<EntryConfirmation />} />
      <Route path="/qr-code" element={<QRCodePage />} />
      <Route path="/shows/:id/results" element={<ShowResults />} />
      <Route path="/shows/:showId/published-results" element={<PublishedResults />} />
      <Route
        path="/scoring/:classId"
        element={
          <ProtectedRoute>
            <ScoringInterface />
          </ProtectedRoute>
        }
      />
      <Route path="/live/:classId" element={<LiveResults />} />
      <Route path="/organizations/:orgId/leaderboard" element={<OrgLeaderboard />} />
      <Route path="/organizations/:orgId/points" element={<OrgPointsAdmin />} />
      <Route path="/organizations/:orgId/standings" element={<OrgStandingsPublic />} />
      <Route
        path="/rider/history"
        element={
          <ProtectedRoute>
            <RiderHistory />
          </ProtectedRoute>
        }
      />
      <Route path="/series/public" element={<SeriesList />} />
      <Route path="/series/:id" element={<SeriesDetail />} />
      <Route path="/kiosk" element={<KioskLogin />} />
      <Route path="/kiosk/enter" element={<KioskEntry />} />
      <Route path="/kiosk/timekeeper" element={<KioskTimekeeper />} />
      <Route path="/kiosk/office" element={<KioskOffice />} />
      <Route
        path="/admin/subscriptions"
        element={
          <ProtectedRoute>
            <SubscriptionAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/platform-settings"
        element={
          <ProtectedRoute>
            <PlatformSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/disputes"
        element={
          <ProtectedRoute>
            <ProducerDisputes />
          </ProtectedRoute>
        }
      />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route
        path="/series"
        element={
          <ProtectedRoute>
            <SeriesManage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <OfflineBanner />
          <VersionBanner />
          <GlobalNav />
          <AppRoutes />
          <Toaster position="top-right" />
          <InstallPrompt />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
